import { cacheKeys } from "@constants/cache-keys";
import { API_BASE_URL } from "@constants/urls";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import authAxios from "@helpers/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Document, PodcastsResponse } from "../types";

const generateHash = (text: string): number => {
	return text.split("").reduce((acc, char) => {
		return acc + char.charCodeAt(0) * 31;
	}, 0);
};

const getPodcasts = async (
	collectionId: number,
): Promise<PodcastsResponse["documents"]> => {
	const response = await authAxios.post(
		`${API_BASE_URL}/user-events-v2/v2/get-documents`,
		{
			collectionId,
		},
	);

	return response.data.data.documents;
};

export const usePodcasts = () => {
	const { currentExamContext } = useExamContext();
	const collectionId = currentExamContext.documentsCollectionId;
	const { data: originalPodcasts, isLoading } = useQuery({
		queryKey: [cacheKeys.podcasts],
		queryFn: () => (collectionId ? getPodcasts(collectionId) : []),
		enabled: !!collectionId,
	});

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const podcastsByType = useMemo(() => {
		const grouped = new Map<string, Document[]>();

		for (const podcast of originalPodcasts ?? []) {
			const topics = podcast.metadata.topics;
			if (Array.isArray(topics) && topics.length > 0) {
				const podcastIdSet = new Set<number>();
				for (const topic of topics) {
					let podcastId = podcast.id;
					const isDuplicate = podcastIdSet.has(podcast.id);
					if (isDuplicate) {
						podcastId = podcast.id + generateHash(topic);
					}
					podcastIdSet.add(podcastId);
					if (!grouped.has(topic)) {
						grouped.set(topic, []);
					}
					grouped.get(topic)!.push({ ...podcast, id: podcastId });
				}
			} else {
				if (!grouped.has("Uncategorized")) {
					grouped.set("Uncategorized", []);
				}
				grouped.get("Uncategorized")!.push({ ...podcast, id: podcast.id });
			}
		}

		return Array.from(grouped).map(([type, podcasts]) => {
			return {
				type,
				podcasts,
			};
		});
	}, [originalPodcasts]);

	const podcasts = useMemo(() => {
		return podcastsByType.flatMap((category) => category.podcasts);
	}, [podcastsByType]);

	return {
		originalPodcasts,
		podcastsByType,
		isLoading,
		podcasts,
	};
};
